import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
} from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import CodesetSelect from "../../CodesetSelect";
import UuidPreview from "../../UuidPreview";
import ReferencesEditor from "../../ReferencesEditor";
import ClassificationsEditor from "../../ClassificationsEditor";
import AmountsEditor from "../../AmountsEditor";
import DateTimesEditor from "../../DateTimesEditor";
import { createEmptySchedule, createEmptyScheduleEntry } from "../../utils/placementDefaults";

const ScheduleEntryEditor = ({ entry, entryIndex, onUpdate, onRemove }) => {
  const updateField = (field, value) => {
    onUpdate(entryIndex, field, value);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={4} borderColor="gray.300">
      <UuidPreview uuid={entry.id} />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>Sequence Number</FormLabel>
          <Input
            type="number"
            value={entry.sequenceNbr}
            onChange={(e) =>
              updateField("sequenceNbr", parseInt(e.target.value) || 0)
            }
          />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            value={entry.description || ""}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <Tabs size="sm" variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>References</Tab>
          <Tab>Classifications</Tab>
          <Tab>Amounts</Tab>
          <Tab>DateTimes</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <ReferencesEditor
              filter="#ScheduleEntry"
              references={entry.references || []}
              onChange={(refs) => updateField("references", refs)}
            />
          </TabPanel>

          <TabPanel>
            <ClassificationsEditor
              filter="#ScheduleEntry"
              classifications={entry.classifications || []}
              onChange={(classifications) =>
                updateField("classifications", classifications)
              }
            />
          </TabPanel>

          <TabPanel>
            <AmountsEditor
              filter="#ScheduleEntry"
              amounts={entry.amounts || []}
              onChange={(amounts) => updateField("amounts", amounts)}
            />
          </TabPanel>

          <TabPanel>
            <DateTimesEditor
              dateTimes={entry.dateTimes || []}
              onChange={(dateTimes) => updateField("dateTimes", dateTimes)}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <HStack justify="flex-end" mt={4}>
        <IconButton
          size="sm"
          icon={<HiOutlineTrash />}
          aria-label="Delete Entry"
          onClick={() => onRemove(entryIndex)}
        />
      </HStack>
    </Box>
  );
};

const ScheduleEditor = ({ schedule, scheduleIndex, onUpdate, onRemove }) => {
  const updateField = (field, value) => {
    onUpdate(scheduleIndex, field, value);
  };

  const updateEntry = (entryIndex, field, value) => {
    const updatedEntries = [...(schedule.entries || [])];
    updatedEntries[entryIndex] = {
      ...updatedEntries[entryIndex],
      [field]: value,
    };
    updateField("entries", updatedEntries);
  };

  const addEntry = () => {
    const updatedEntries = [...(schedule.entries || []), createEmptyScheduleEntry()];
    updateField("entries", updatedEntries);
  };

  const removeEntry = (entryIndex) => {
    const updatedEntries = [...schedule.entries];
    updatedEntries.splice(entryIndex, 1);
    updateField("entries", updatedEntries);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={6} borderColor="gray.500">
      <UuidPreview uuid={schedule.id} />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <CodesetSelect
          label="Type"
          codesetCode="7510"
          value={schedule.type ? { code: schedule.type, description: "" } : null}
          onChange={(value) => updateField("type", value.code)}
        />

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            value={schedule.description || ""}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <Box mt={4}>
        <HStack mb={2}>
          <Heading size="sm">Entries</Heading>
          <IconButton
            icon={<HiOutlinePlus />}
            size="sm"
            aria-label="Add Entry"
            onClick={addEntry}
          />
        </HStack>

        {(schedule.entries || []).map((entry, entryIndex) => (
          <ScheduleEntryEditor
            key={entry.id}
            entry={entry}
            entryIndex={entryIndex}
            onUpdate={updateEntry}
            onRemove={removeEntry}
          />
        ))}
      </Box>

      <HStack justify="flex-end" mt={4}>
        <IconButton
          size="md"
          icon={<HiOutlineTrash />}
          aria-label="Delete Schedule"
          onClick={() => onRemove(scheduleIndex)}
        />
      </HStack>
    </Box>
  );
};

const SchedulesEditor = ({ schedules = [], onChange }) => {
  const updateSchedule = (scheduleIndex, field, value) => {
    const updated = [...schedules];
    updated[scheduleIndex] = {
      ...updated[scheduleIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addSchedule = () => {
    onChange([...schedules, createEmptySchedule()]);
  };

  const removeSchedule = (scheduleIndex) => {
    const updated = [...schedules];
    updated.splice(scheduleIndex, 1);
    onChange(updated);
  };

  return (
    <Box>
      <HStack alignItems="center" mb={4}>
        <Heading size="md">Schedules</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="md"
          aria-label="Add Schedule"
          onClick={addSchedule}
        />
      </HStack>

      {schedules.map((schedule, scheduleIndex) => (
        <ScheduleEditor
          key={schedule.id}
          schedule={schedule}
          scheduleIndex={scheduleIndex}
          onUpdate={updateSchedule}
          onRemove={removeSchedule}
        />
      ))}
    </Box>
  );
};

export default SchedulesEditor;
